import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { putDraft, getAllDrafts, deleteDraft } from "@/lib/db";

const SyncContext = createContext(null);

export function SyncProvider({ children }) {
  const [drafts, setDrafts] = useState([]);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(localStorage.getItem("orbit_last_sync") || null);
  const syncingRef = useRef(false);

  const reload = useCallback(async () => {
    setDrafts(await getAllDrafts());
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const syncAll = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    const pending = (await getAllDrafts()).filter((d) => d.status !== "archived");
    if (pending.length === 0) return;
    syncingRef.current = true;
    setSyncing(true);
    let didSync = false;
    for (const d of pending) {
      try {
        await api.post("/work-entries", { ...d.payload, source: "offline_sync" });
        await deleteDraft(d.id);
        didSync = true;
      } catch (err) {
        const status = err.response?.status;
        const detail = formatApiError(err.response?.data?.detail);
        if (status === 409) {
          await putDraft({ ...d, status: "conflict", error: detail });
        } else if (status === 400 && /archiviat/i.test(detail || "")) {
          await putDraft({ ...d, status: "archived", error: "Impossibile sincronizzare: il mese è stato archiviato. Contatta l'Ufficio." });
        } else if (status === 400) {
          await putDraft({ ...d, status: "conflict", error: detail });
        }
        // errori di rete: lascia pending
      }
    }
    if (didSync) {
      const ts = new Date().toISOString();
      setLastSync(ts);
      localStorage.setItem("orbit_last_sync", ts);
      toast.success("Registrazione sincronizzata", { duration: 2000 });
    }
    syncingRef.current = false;
    setSyncing(false);
    await reload();
  }, [reload]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); syncAll(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) syncAll();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [syncAll]);

  const addDraft = useCallback(async (payload, meta) => {
    // meta: { service_type, entity_id }
    const draft = {
      id: `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      payload, ...meta, status: "pending", error: null, created_at: new Date().toISOString(),
    };
    await putDraft(draft);
    await reload();
    if (navigator.onLine) syncAll();
    return draft;
  }, [reload, syncAll]);

  const removeDraft = useCallback(async (id) => {
    await deleteDraft(id);
    await reload();
  }, [reload]);

  const pendingCount = drafts.filter((d) => d.status === "pending").length;
  const errorCount = drafts.filter((d) => d.status === "conflict" || d.status === "archived").length;

  let indicator = "synced";
  if (syncing) indicator = "syncing";
  else if (errorCount > 0) indicator = "error";
  else if (pendingCount > 0) indicator = "pending";

  const draftsFor = (service_type, entity_id) =>
    drafts.filter((d) => d.service_type === service_type && d.entity_id === entity_id);

  return (
    <SyncContext.Provider value={{
      drafts, online, syncing, lastSync, indicator, pendingCount, errorCount,
      addDraft, removeDraft, syncAll, reload, draftsFor,
    }}>
      {children}
    </SyncContext.Provider>
  );
}

export const useSync = () => useContext(SyncContext);
